/**
 * Redux async actions (src/store/actions/paymentActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { toAdminRequestError } from "../../utils/adminRequestError";

// ACTIONS : api calls
/**
 * Async thunk: fetchAllpayments.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchAllpayments = createAsyncThunk(
  "payments/getpayments",
  async (params = {}) => {
    const response = await axios.get(`getpayments`, { params });
    return response.data;
  }
);

/**
 * Async thunk: Add_payment.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Add_payment = createAsyncThunk(
  "payments/Add_payment",
  async () => {
    const response = await axios.post(`/Add_Payment`);
    return response.data;
  }
);

/**
 * Async thunk: Deletepayment.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Deletepayment = createAsyncThunk(
  "payments/Deletepayment",
  async (PaymentID) => {
    try {
      const response = await axios.delete(`Delete_Payment/${PaymentID}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: getPaymentStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getPaymentStatus = createAsyncThunk(
  "payments/GetPaymentStatus",
  async (OrderId) => {
    try {
      const response = await axios.get(`getStatusPayment/${OrderId}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: Updatepayment.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Updatepayment = createAsyncThunk(
  "payments/Updatepayment",
  async ({ PaymentID, updatedData }) => {
    try {
      const response = await axios.post(
        `Update_Payment/${PaymentID}`,
        updatedData
      );
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: GetPaymentsByStudentID.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetPaymentsByStudentID = createAsyncThunk(
  "payments/GetPaymentsByStudentID",
  async (id) => {
    try {
      const response = await axios.get(`GetPaymentsByStudentID/${id}`);
      return response.data.Payments;
    } catch (error) {
      console.error("Error fetching payments:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: Create_Payment.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Create_Payment = createAsyncThunk(
  "packages/Create_Payment",
  async (formData) => {
    try {
      const response = await axios.post(`Make-Payment`, formData);
      document.cookie = `orderId=${response.data.orderId}; path=/`;
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: getOrderDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getOrderDetails = createAsyncThunk(
  "packages/getOrderDetails",
  async (orderId) => {
    try {
      const response = await axios.get(`authorizePayment/${orderId}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: SearchPaymentbyStudentUsername.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const SearchPaymentbyStudentUsername = createAsyncThunk(
  "packages/SearchPaymentbyStudentUsername",
  async (input) => {
    try {
      const response = await axios.post(`SearchPaymentbyStudentUsername/${input}`);
      return response.data;
    } catch (error) {

      throw error;
    }
  }
);

/**
 * Async thunk: storePaymentDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const storePaymentDetails = createAsyncThunk(
  "payments/storePaymentDetails",
  async (paymentData) => {
    try {
      const response = await axios.post(`payment-details`, paymentData);
      return response.data;
    } catch (error) {
      console.error("Error storing payment details:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: fetchPaymentDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchPaymentDetails = createAsyncThunk(
  "payments/fetchPaymentDetails",
  async (params = {}) => {
    try {
      const { page = 1, limit = 10, studentId, status, search, scope } = params;

      // Try legacy endpoint first since we have legacy data
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      if (studentId) queryParams.append('studentId', studentId);
      if (status && status !== 'all') queryParams.append('status', status);
      if (search) queryParams.append('search', search);
      if (scope) queryParams.append('scope', scope);

      const response = await axios.get(`payment-details?${queryParams.toString()}`);

      return {
        ...response.data,
        source: 'legacy'
      };

    } catch (error) {
      console.error("Error fetching payment details:", error);
      throw error;
    }
  }
);

export const fetchAccountantDashboardPayments = createAsyncThunk(
  "payments/fetchAccountantDashboardPayments",
  async ({ scope } = {}) => {
    const response = await axios.get("accountant/dashboard/payments", {
      params: { scope, limit: 6 },
    });
    return response.data;
  }
);

// New action specifically for Infakt invoice management
/**
 * Async thunk: fetchInfaktPayments.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchInfaktPayments = createAsyncThunk(
  "payments/fetchInfaktPayments",
  async (params = {}) => {
    try {
      const { page = 1, limit = 10, student_id, status } = params;
      const queryParams = new URLSearchParams();

      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      if (student_id) queryParams.append('student_id', student_id);
      if (status && status !== 'all') queryParams.append('status', status);

      const response = await axios.get(`infakt/payments?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Infakt payments:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: fetch one accountant payment by its payment id.
 *
 * The invoice view is a deep-link target, so it must hydrate the exact
 * record instead of downloading a broad page and searching it locally.
 */
export const fetchInfaktPaymentById = createAsyncThunk(
  "payments/fetchInfaktPaymentById",
  async (paymentId, { rejectWithValue }) => {
    if (!paymentId) {
      throw new Error("Missing payment id");
    }

    try {
      const response = await axios.get(`infakt/payments/${encodeURIComponent(paymentId)}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, "Invoice request failed"));
    }
  }
);

/**
 * Async thunk: getPayuAuthToken.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getPayuAuthToken = createAsyncThunk(
  "payments/getPayuAuthToken",
  async () => {
    try {
      const response = await axios.get(`payu/token`);
      return {
        success: true,
        token: response.data.token.access_token, // Fix the path
        tokenType: response.data.token.token_type,
        expiresIn: response.data.token.expires_in
      };
    } catch (error) {
      console.error("Error getting PayU auth token:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: getPayuPaymentMethods.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getPayuPaymentMethods = createAsyncThunk(
  "payments/getPayuPaymentMethods",
  async () => {
    try {
      const response = await axios.get(`payu/payment-methods`);
      // Return the response exactly as received
      return response.data;
    } catch (error) {
      console.error("Error getting PayU payment methods:", error);
      throw error;
    }
  }
);

/**
 * Async thunk: manualAddWalletHours.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const manualAddWalletHours = createAsyncThunk(
  "payments/manualAddWalletHours",
  async (walletData) => {
    try {
      const response = await axios.post(`manual-add-wallet-hours`, walletData);
      return response.data;
    } catch (error) {
      console.error("Error manually adding wallet hours:", error);
      throw error;
    }
  }
);

// Create Infakt Invoice
/**
 * Async thunk: createInfaktInvoice.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createInfaktInvoice = createAsyncThunk(
  "payments/createInfaktInvoice",
  async (invoiceData, { rejectWithValue }) => {
    try {

      const response = await axios.post('infakt/invoices', invoiceData);
      return response.data;
    } catch (error) {
      console.error('Error creating Infakt invoice:', error);
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to create invoice',
        error: error.response?.data?.error || error.message
      });
    }
  }
);

// Download invoice PDF using the numeric Infakt invoice id.
/**
 * Async thunk: downloadInfaktInvoicePDF.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const downloadInfaktInvoicePDF = createAsyncThunk(
  "payments/downloadInfaktInvoicePDF",
  async ({ invoiceId, infakt_uuid, invoiceNumber, paymentId }, { getState, rejectWithValue }) => {
    try {
      const resolvedInvoiceId = invoiceId || infakt_uuid;
      if (!resolvedInvoiceId) {
        throw new Error('Missing invoice id');
      }

      const currentUser = getState()?.students?.user || {};
      const userRole = String(
        currentUser.role || currentUser.type || currentUser.UserType || ''
      ).toLowerCase();
      const isStudent = userRole === 'student';
      const accountType = String(
        currentUser.accountType || (currentUser.parentUserId ? 'child' : 'parent')
      ).trim().toLowerCase();

      if (isStudent && accountType === 'child') {
        return rejectWithValue({
          paymentId,
          status: 403,
          code: 'CHILD_PAYMENT_FORBIDDEN',
          message: 'Child accounts cannot access payments',
        });
      }

      const isLinkedInvoice = String(paymentId || '').startsWith('linked-infakt-');
      const requestUrl = isStudent
        ? (isLinkedInvoice
            ? `infakt/my-linked-invoices/${resolvedInvoiceId}/pdf`
            : paymentId
              ? `infakt/my-payments/${paymentId}/pdf`
              : `infakt/invoices/${resolvedInvoiceId}/pdf`)
        : `infakt/invoices/${resolvedInvoiceId}/pdf`;

      const response = await axios({
        method: 'get',
        url: requestUrl,
        responseType: 'blob',
        timeout: 30000 // 30 second timeout
      });

      // Check if response is valid
      if (response.data.size === 0) {
        throw new Error('Empty invoice file received');
      }

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Generate filename with invoice details and date
      const date = new Date().toISOString().split('T')[0];
      const filename = `invoice-${invoiceNumber || resolvedInvoiceId}-${date}.pdf`;

      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        filename,
        paymentId,
        message: 'Invoice downloaded successfully!'
      };

    } catch (error) {
      console.error('Invoice download error:', error);

      // Handle different error types
      let errorMessage = 'Failed to download invoice';
      let errorDescription = 'Please try again later or contact support if the problem persists.';
      const status = Number(error.response?.status || 0) || null;

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Download Timeout';
        errorDescription = 'The download is taking too long. Please check your connection and try again.';
      } else if (status === 409) {
        errorMessage = 'Invoice Not Ready';
        errorDescription = 'The invoice is still being prepared. Refresh the payment history before trying again.';
      } else if (status === 404) {
        errorMessage = 'Invoice Not Found';
        errorDescription = 'The invoice could not be found in the system. It may still be processing or be stored under a different provider.';
      } else if (status === 401 || status === 403) {
        errorMessage = 'Access Denied';
        errorDescription = 'You do not have permission to download this invoice.';
      } else if (status >= 500) {
        errorMessage = 'Server Error';
        errorDescription = 'There was a problem with our servers. Please try again in a few minutes.';
      }

      return rejectWithValue({
        message: errorMessage,
        description: errorDescription,
        paymentId,
        status
      });
    }
  }
);

// Mark Infakt Invoice as Paid
/**
 * Async thunk: markInfaktInvoiceAsPaid.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const markInfaktInvoiceAsPaid = createAsyncThunk(
  "payments/markInfaktInvoiceAsPaid",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`infakt/invoices/${invoiceId}/paid`);
      return response.data;
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      return rejectWithValue({
        message: error.response?.data?.message || 'Failed to mark invoice as paid',
        error: error.response?.data?.error || error.message
      });
    }
  }
);
